import { Helmet } from "react-helmet-async";
import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronLeft, CalendarDays, ChevronRight, X } from "lucide-react";
import { fetchPublishedEventBySlug } from "@/services/eventService";
import { useLanguage } from "@/context/LanguageContext";

const EventDetails = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language, tl } = useLanguage();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const { scrollYProgress } = useScroll();
  const heroOffsetY = useTransform(scrollYProgress, [0, 0.25], [0, 80]);

  const { data: event, isLoading } = useQuery({
    queryKey: ["events", "published", slug],
    queryFn: () => fetchPublishedEventBySlug(slug ?? ""),
    enabled: Boolean(slug),
  });

  const lightboxImage = useMemo(() => {
    if (selectedImageIndex === null || !event) return null;
    return event.images[selectedImageIndex] ?? null;
  }, [selectedImageIndex, event]);

  const goToPreviousImage = () => {
    if (!event || selectedImageIndex === null) return;
    setSelectedImageIndex((selectedImageIndex - 1 + event.images.length) % event.images.length);
  };

  const goToNextImage = () => {
    if (!event || selectedImageIndex === null) return;
    setSelectedImageIndex((selectedImageIndex + 1) % event.images.length);
  };

  if (!slug) {
    return <Navigate to="/events" replace />;
  }

  useEffect(() => {
    if (selectedImageIndex === null || !event) return;

    const handleKeyDown = (eventKey: KeyboardEvent) => {
      if (eventKey.key === "Escape") {
        setSelectedImageIndex(null);
      } else if (eventKey.key === "ArrowLeft") {
        setSelectedImageIndex((current) =>
          current === null ? null : (current - 1 + event.images.length) % event.images.length,
        );
      } else if (eventKey.key === "ArrowRight") {
        setSelectedImageIndex((current) =>
          current === null ? null : (current + 1) % event.images.length,
        );
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedImageIndex, event]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center text-gray-500">
          {tl("Chargement de l'evenement...", "Loading event...", "Caricamento dell'evento...")}
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-xl">
          <h1 className="text-3xl md:text-4xl font-normal text-gray-900">
            {tl('Evenement introuvable', 'Event not found', 'Evento non trovato')}
          </h1>
          <p className="mt-3 text-gray-600">
            {tl(
              "L'evenement demande n'existe pas ou n'est plus disponible.",
              'The requested event does not exist or is no longer available.',
              "L'evento richiesto non esiste o non è più disponibile."
            )}
          </p>
          <Link
            to="/events"
            className="mt-8 inline-flex items-center gap-2 border border-gray-300 px-5 py-2.5 text-sm uppercase tracking-[0.15em] text-gray-900 hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            {tl('Retour aux evenements', 'Back to events', 'Torna agli eventi')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{`${event.title[language]} | Meubles Karim`}</title>
      </Helmet>

      <section className="relative h-[48vh] md:h-[62vh] overflow-hidden">
        <motion.img
          src={event.coverImage || event.images[0]?.url || "/placeholder.svg"}
          alt={event.images[0]?.alt[language] ?? event.title[language]}
          className="h-full w-full object-cover"
          style={{ y: heroOffsetY, scale: 1.08 }}
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 container-custom flex flex-col justify-end pb-10 md:pb-14">
          <Link
            to="/events"
            className="mb-4 inline-flex w-fit items-center gap-2 text-white/90 hover:text-white"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.2em]">
              {tl('Tous les evenements', 'All events', 'Tutti gli eventi')}
            </span>
          </Link>
          <div className="inline-flex items-center gap-2 text-white/85 text-xs md:text-sm uppercase tracking-[0.2em]">
            <CalendarDays className="h-4 w-4" />
            <time dateTime={event.date}>
              {new Date(event.date).toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'it' ? 'it-IT' : 'en-GB', {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </time>
          </div>
          <h1 className="mt-3 text-4xl md:text-6xl text-white font-normal leading-tight">
            {event.title[language]}
          </h1>
        </div>
      </section>

      <motion.section
        className="container-custom py-12 md:py-16"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.55 }}
      >
        <div className="max-w-4xl border-l border-gray-300 pl-5 md:pl-8">
          <p className="text-lg md:text-xl leading-relaxed text-gray-700">
            {event.description[language]}
          </p>
        </div>
      </motion.section>

      <section className="container-custom pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7">
          {event.images.map((image, index) => (
            <motion.button
              key={`${event.id}-${index}`}
              type="button"
              onClick={() => setSelectedImageIndex(index)}
              className={`overflow-hidden rounded-xl ${
                index === 0 ? "md:col-span-2 aspect-[16/8]" : "aspect-[4/3]"
              } group relative`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, delay: index * 0.06 }}
            >
              <img
                src={image.url}
                alt={image.alt[language]}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading={index > 1 ? "lazy" : "eager"}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
            </motion.button>
          ))}
        </div>
      </section>

      {lightboxImage && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4 md:p-8">
          <button
            type="button"
            onClick={() => setSelectedImageIndex(null)}
            className="absolute top-4 right-4 md:top-6 md:right-6 p-2 text-white/80 hover:text-white"
            aria-label="Close lightbox"
          >
            <X className="h-7 w-7" />
          </button>

          <button
            type="button"
            onClick={goToPreviousImage}
            className="absolute left-3 md:left-6 p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10"
            aria-label="Previous image"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>

          <motion.img
            key={lightboxImage.url}
            src={lightboxImage.url}
            alt={lightboxImage.alt[language]}
            className="max-h-[88vh] max-w-[92vw] object-contain"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25 }}
          />

          <button
            type="button"
            onClick={goToNextImage}
            className="absolute right-3 md:right-6 p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10"
            aria-label="Next image"
          >
            <ChevronRight className="h-8 w-8" />
          </button>
        </div>
      )}
    </div>
  );
};

export default EventDetails;
